// ============================================
// Home Page
// ============================================

import { Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Star, Heart, BookOpen, MessageCircle } from 'lucide-react';

export default function HomePage() {
  const { isAuthenticated } = useAuthStore();

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-white font-bold">
              E
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              Equantu Kids
            </span>
          </div>
          <nav className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/chat" className="btn-primary">
                  开始对话
                </Link>
                <Link to="/parent" className="text-gray-600 hover:text-gray-800">
                  家长面板
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-600 hover:text-gray-800">
                  登录
                </Link>
                <Link to="/register" className="btn-primary">
                  免费注册
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              穆斯林儿童的AI陪伴助手
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            让孩子在温暖安全的环境中学习古兰经、听先知故事、探索伊斯兰智慧
          </p>
          <div className="flex justify-center gap-4">
            <Link to={isAuthenticated ? '/chat' : '/register'} className="btn-primary text-lg px-8 py-4">
              {isAuthenticated ? '开始聊天' : '免费开始'}
            </Link>
            <a href="#features" className="btn-secondary text-lg px-8 py-4">
              了解更多
            </a>
          </div>
        </div>
      </section>

      {/* Personas Section */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">认识你的AI朋友们</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Noor */}
            <div className="persona-card persona-card-noor">
              <div className="w-20 h-20 rounded-full bg-white/20 mx-auto mb-4 flex items-center justify-center text-4xl">
                📚
              </div>
              <h3 className="text-2xl font-bold text-center mb-2">Noor老师</h3>
              <p className="text-white/90 text-center">
                温柔耐心的古兰经老师，教你诵读古兰经和学习阿拉伯语
              </p>
            </div>

            {/* Iman */}
            <div className="persona-card persona-card-iman">
              <div className="w-20 h-20 rounded-full bg-white/20 mx-auto mb-4 flex items-center justify-center text-4xl">
                ✨
              </div>
              <h3 className="text-2xl font-bold text-center mb-2">Iman小伙伴</h3>
              <p className="text-white/90 text-center">
                活泼有趣的小伙伴，给你讲精彩的先知故事和冒险故事
              </p>
            </div>

            {/* Hakeem */}
            <div className="persona-card persona-card-hakeem">
              <div className="w-20 h-20 rounded-full bg-white/20 mx-auto mb-4 flex items-center justify-center text-4xl">
                🌙
              </div>
              <h3 className="text-2xl font-bold text-center mb-2">Hakeem爷爷</h3>
              <p className="text-white/90 text-center">
                智慧慈祥的长者，分享伊斯兰历史和人生智慧
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">为什么选择我们</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto mb-4 flex items-center justify-center">
                <Heart className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold mb-2">安全第一</h3>
              <p className="text-gray-600 text-sm">严格的内容过滤，确保对话安全适龄</p>
            </div>

            <div className="card text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold mb-2">伊斯兰教育</h3>
              <p className="text-gray-600 text-sm">古兰经学习、先知故事、伊斯兰礼仪</p>
            </div>

            <div className="card text-center">
              <div className="w-12 h-12 rounded-full bg-purple-100 mx-auto mb-4 flex items-center justify-center">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold mb-2">个性化记忆</h3>
              <p className="text-gray-600 text-sm">记住孩子的学习进度和偏好</p>
            </div>

            <div className="card text-center">
              <div className="w-12 h-12 rounded-full bg-orange-100 mx-auto mb-4 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-bold mb-2">语音交互</h3>
              <p className="text-gray-600 text-sm">支持语音对话，像真正的朋友一样</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-500 to-green-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">准备好开始了吗？</h2>
          <p className="text-lg opacity-90 mb-8">让您的孩子在快乐中学习伊斯兰知识</p>
          <Link
            to={isAuthenticated ? '/chat' : '/register'}
            className="inline-block px-8 py-4 bg-white text-blue-600 rounded-xl font-bold hover:bg-gray-100 transition-colors"
          >
            {isAuthenticated ? '开始对话' : '免费注册'}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-800 text-gray-400">
        <div className="max-w-6xl mx-auto text-center">
          <p>© 2025 Equantu Kids AI. 为穆斯林家庭打造。</p>
        </div>
      </footer>
    </div>
  );
}
